// src/pages/ELearning/LearningPathDetailPage.tsx — детали + прогресс
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getElearningPathById, startElearningPath, assignElearningPath, getCompanyUsers } from '../../api/client';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import {
  Loader2, ArrowLeft, Route, CheckCircle, Clock, Lock, Play,
  Award, User as UserIcon, Calendar, ChevronRight
} from 'lucide-react';

const LearningPathDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showToast } = useToast();
  const isHR = ['admin', 'superadmin', 'hr_manager'].includes(user?.role || '');

  const [showAssignModal, setShowAssignModal] = useState(false);
  const [employees, setEmployees] = useState<any[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
  const [assigning, setAssigning] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const d = await getElearningPathById(id!);
        setData(d);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, [id]);

  const handleStart = async () => {
    setStarting(true);
    try {
      await startElearningPath(id!);
      const d = await getElearningPathById(id!);
      setData(d);
    } catch (e: any) {
      alert('Ошибка: ' + (e.response?.data?.error || e.message));
    } finally { setStarting(false); }
  };

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}>
      <Loader2 size={40} color="#667eea" style={{ animation: 'spin 1s linear infinite' }} />
    </div>
  );
  if (!data?.path) return <p>Программа не найдена</p>;

  const path = data.path;
  const myProgress = data.myProgress;
  const steps = [...(path.steps || [])].sort((a: any, b: any) => a.order - b.order);
  const stepProgress: any[] = myProgress?.steps || [];

  const getStepStatus = (step: any, idx: number) => {
    if (!myProgress) return { status: 'not_started', label: 'Не начато', color: '#8b8fa8', bg: 'rgba(139,143,168,0.12)' };
    const sp = stepProgress.find((s: any) => String(s.testId?._id || s.testId) === String(step.testId?._id || step.testId));
    if (!sp) return { status: 'locked', label: 'Заблокировано', color: '#8b8fa8', bg: 'rgba(139,143,168,0.12)' };

    if (sp.status === 'completed') return { status: 'completed', label: 'Пройдено', color: '#10b981', bg: 'rgba(16,185,129,0.12)' };
    if (sp.status === 'failed') return { status: 'failed', label: 'Не пройден', color: '#ef4444', bg: 'rgba(239,68,68,0.12)' };
    if (sp.status === 'in_progress') return { status: 'in_progress', label: 'В работе', color: '#667eea', bg: 'rgba(102,126,234,0.12)' };
    if (sp.status === 'available') return { status: 'available', label: 'Доступно', color: '#667eea', bg: 'rgba(102,126,234,0.12)' };
    return { status: 'locked', label: 'Заблокировано', color: '#8b8fa8', bg: 'rgba(139,143,168,0.12)' };
  };


  const openAssignModal = async () => {
    setShowAssignModal(true);
    setSelectedUsers(new Set());
    if (employees.length === 0) {
      setLoadingUsers(true);
      try {
        const res: any = await getCompanyUsers();
        const items: any[] = Array.isArray(res)
          ? res
          : (res?.items || res?.users || res?.data || []);
        // ★ Backend возвращает `id`, а не `_id`
        const meId = String((user as any)?._id || (user as any)?.id || '');
        const filtered = items
          .map((u: any) => ({
            id: String(u.id || u._id),
            name: u.name || u.email || 'Без имени',
            email: u.email || '',
            role: u.role || '',
          }))
          .filter((u: any) => u.id && u.id !== meId);
        setEmployees(filtered);
      } catch (e) {
        console.error('[assign] error:', e);
        showToast('Ошибка загрузки сотрудников', 'error');
      } finally {
        setLoadingUsers(false);
      }
    }
  };

  const toggleUser = (userId: string) => {
    setSelectedUsers(prev => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedUsers.size === employees.length) {
      setSelectedUsers(new Set());
    } else {
      setSelectedUsers(new Set(employees.map(e => e.id)));
    }
  };

  const handleAssign = async () => {
    if (selectedUsers.size === 0 || !id) return;
    setAssigning(true);
    try {
      const res = await assignElearningPath(id, Array.from(selectedUsers));
      showToast('Назначено: ' + (res?.created || selectedUsers.size) + ' сотрудникам', 'success');
      setShowAssignModal(false);
      setSelectedUsers(new Set());
    } catch (e: any) {
      showToast('Ошибка: ' + (e.response?.data?.error || e.message), 'error');
    } finally {
      setAssigning(false);
    }
  };

  return (
    <>
      <style>{`
        @keyframes fadeUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
        .lp-hero { animation: fadeUp 0.5s ease-out; }
        .lp-step { animation: fadeUp 0.5s ease-out backwards; transition: all 0.3s ease; }
        .lp-step:hover { transform: translateX(4px); }
      `}</style>

      <div style={{ marginBottom: 20 }}>
        <button onClick={() => navigate('/app/elearning/paths')} style={styles.backBtn}>
          <ArrowLeft size={16} /> К программам
        </button>
      </div>

      <div className="lp-hero" style={styles.hero}>
        <div style={styles.heroIcon}>
          <Route size={32} color="#fff" />
        </div>
        <div style={{ flex: 1 }}>
          <h1 style={styles.title}>{path.name}</h1>
          {path.description && <p style={styles.subtitle}>{path.description}</p>}
          <div style={styles.heroMeta}>
            <span style={styles.metaChip}>{steps.length} шагов</span>
            {path.mandatory && <span style={{ ...styles.metaChip, background: 'rgba(239,68,68,0.12)', color: '#ef4444' }}>🔴 Обязательна</span>}
            {path.isOnboarding && <span style={{ ...styles.metaChip, background: 'rgba(16,185,129,0.12)', color: '#10b981' }}>✨ Онбординг</span>}
          </div>
        </div>
        {isHR && (
          <button
            onClick={openAssignModal}
            style={{
              padding: '10px 20px',
              background: 'linear-gradient(135deg, #10b981, #059669)',
              color: '#fff',
              border: 'none',
              borderRadius: 12,
              fontWeight: 700,
              fontSize: 14,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              boxShadow: '0 4px 12px rgba(16,185,129,0.25)',
              marginRight: 10,
            }}
          >
            👥 Назначить
          </button>
        )}
        {myProgress && myProgress.status === 'not_started' && (
          <button onClick={handleStart} disabled={starting} style={styles.startBtn}>
            {starting ? <Loader2 size={18} className="spin" style={{ animation: 'spin 1s linear infinite' }} /> : <Play size={18} />}
            {starting ? 'Запуск...' : 'Начать'}
          </button>
        )}
      </div>

      {/* Прогресс */}
      {myProgress && (
        <div style={styles.progressCard}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10, fontSize: 14 }}>
            <span style={{ fontWeight: 700, color: '#5a5f7a' }}>
              Прогресс: {stepProgress.filter((s: any) => s.status === 'completed').length} / {steps.length}
            </span>
            <span style={{ fontWeight: 800, color: '#667eea' }}>
              {Math.round((stepProgress.filter((s: any) => s.status === 'completed').length / steps.length) * 100)}%
            </span>
          </div>
          <div style={styles.progressBar}>
            <div style={{
              ...styles.progressFill,
              width: `${(stepProgress.filter((s: any) => s.status === 'completed').length / Math.max(steps.length, 1)) * 100}%`,
            }} />
          </div>
        </div>
      )}

      {/* Шаги */}
      <h2 style={styles.sectionTitle}>Шаги программы</h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {steps.map((step: any, idx: number) => {
          const st = getStepStatus(step, idx);
          const sp = stepProgress.find((s: any) => String(s.testId?._id || s.testId) === String(step.testId?._id || step.testId));
          const canStart = st.status === 'available' || st.status === 'failed' || st.status === 'in_progress';

          return (
            <div
              key={step._id || idx}
              className="lp-step"
              style={{ ...styles.stepCard, animationDelay: `${idx * 0.05}s` }}
              onClick={() => canStart && step.testId?._id && navigate(`/app/elearning/tests/${step.testId._id}`)}
            >
              <div style={{ ...styles.stepNum, background: st.bg, color: st.color }}>
                {st.status === 'completed' ? <CheckCircle size={18} /> :
                 st.status === 'locked' ? <Lock size={16} /> :
                 <span style={{ fontWeight: 800 }}>{idx + 1}</span>}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={styles.stepTitle}>{step.testId?.title || 'Тест'}</div>
                <div style={styles.stepMeta}>
                  {step.testId?.category && <span>📁 {step.testId.category}</span>}
                  {step.testId?.timeLimit && <span>⏱ {step.testId.timeLimit} мин</span>}
                  {step.deadlineDays && <span>📅 до {step.deadlineDays} дн.</span>}
                </div>
                {sp?.score != null && (
                  <div style={{ fontSize: 11, color: '#8b8fa8', marginTop: 4 }}>
                    Результат: <strong style={{ color: st.color }}>{sp.score}%</strong>
                  </div>
                )}
              </div>
              <div style={{
                padding: '5px 12px',
                background: st.bg,
                color: st.color,
                borderRadius: 10,
                fontSize: 11,
                fontWeight: 800,
                flexShrink: 0,
              }}>
                {st.label}
              </div>
              {canStart && <ChevronRight size={18} color="#667eea" />}
            </div>
          );
        })}
      </div>

      {/* ═══ Модалка назначения ═══ */}
      {showAssignModal && (
        <div
          onClick={() => setShowAssignModal(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 20,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: '#fff', borderRadius: 20, padding: 24,
              maxWidth: 500, width: '100%', maxHeight: '80vh',
              display: 'flex', flexDirection: 'column',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h2 style={{ fontSize: 18, fontWeight: 800, margin: 0 }}>
                👥 Назначить программу
              </h2>
              <button
                onClick={() => setShowAssignModal(false)}
                style={{
                  border: 'none', background: 'rgba(0,0,0,0.05)',
                  width: 32, height: 32, borderRadius: 10,
                  cursor: 'pointer', fontSize: 16,
                }}
              >
                ✕
              </button>
            </div>
            <p style={{ fontSize: 13, color: '#666', marginBottom: 16 }}>
              «{path.name}» — {steps.length} шагов
            </p>

            {loadingUsers ? (
              <div style={{ textAlign: 'center', padding: 40 }}>
                <Loader2 size={32} style={{ animation: 'spin 1s linear infinite' }} />
              </div>
            ) : (
              <>
                <button
                  onClick={toggleAll}
                  style={{
                    padding: '10px 16px', marginBottom: 12,
                    background: 'rgba(102,126,234,0.08)', border: 'none',
                    borderRadius: 10, cursor: 'pointer', fontWeight: 600,
                    fontSize: 13, color: '#667eea', textAlign: 'left',
                  }}
                >
                  {selectedUsers.size === employees.length ? '☐ Снять все' : '☑ Выбрать всех (' + employees.length + ')'}
                </button>

                <div style={{ flex: 1, overflowY: 'auto', marginBottom: 16 }}>
                  {employees.map((emp: any) => (
                    <label
                      key={emp.id}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 12,
                        padding: '10px 12px', borderRadius: 10,
                        cursor: 'pointer',
                        background: selectedUsers.has(emp._id) ? 'rgba(16,185,129,0.08)' : 'transparent',
                        transition: 'background 0.15s',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedUsers.has(emp.id)}
                        onChange={() => toggleUser(emp.id)}
                        style={{ width: 18, height: 18, cursor: 'pointer' }}
                      />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: 14 }}>{emp.name || emp.email}</div>
                        <div style={{ fontSize: 12, color: '#888' }}>{emp.role}{emp.department ? ' · ' + emp.department : ''}</div>
                      </div>
                    </label>
                  ))}
                </div>

                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => setShowAssignModal(false)}
                    style={{
                      padding: '10px 20px', border: '1px solid rgba(0,0,0,0.1)',
                      background: '#fff', borderRadius: 10, cursor: 'pointer',
                      fontWeight: 600, fontSize: 14,
                    }}
                  >
                    Отмена
                  </button>
                  <button
                    onClick={handleAssign}
                    disabled={selectedUsers.size === 0 || assigning}
                    style={{
                      padding: '10px 20px', border: 'none',
                      background: selectedUsers.size === 0 ? '#ccc' : 'linear-gradient(135deg, #10b981, #059669)',
                      color: '#fff', borderRadius: 10, cursor: selectedUsers.size === 0 ? 'not-allowed' : 'pointer',
                      fontWeight: 700, fontSize: 14,
                      display: 'flex', alignItems: 'center', gap: 6,
                    }}
                  >
                    {assigning ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> : null}
                    {assigning ? 'Назначение...' : 'Назначить (' + selectedUsers.size + ')'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

const styles: Record<string, React.CSSProperties> = {
  backBtn: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 16px', background: 'rgba(255,255,255,0.7)', color: '#5a5f7a', borderRadius: 12, border: '1px solid rgba(0,0,0,0.08)', fontWeight: 700, fontSize: 13, cursor: 'pointer' },

  hero: { display: 'flex', gap: 16, alignItems: 'flex-start', marginBottom: 24, flexWrap: 'wrap' },
  heroIcon: { width: 64, height: 64, borderRadius: 20, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 12px 32px rgba(102,126,234,0.4)', flexShrink: 0 },
  title: { fontSize: 28, fontWeight: 800, margin: 0, color: '#1a1a2e' },
  subtitle: { fontSize: 14, color: '#8b8fa8', margin: '4px 0 8px' },
  heroMeta: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  metaChip: { display: 'inline-flex', alignItems: 'center', padding: '4px 10px', background: 'rgba(102,126,234,0.12)', color: '#667eea', borderRadius: 10, fontSize: 11, fontWeight: 700 },

  startBtn: { padding: '12px 22px', background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)', color: '#fff', border: 'none', borderRadius: 14, fontWeight: 800, fontSize: 14, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 8, boxShadow: '0 8px 24px rgba(17,153,142,0.4)', flexShrink: 0 },

  progressCard: { background: 'rgba(255,255,255,0.7)', borderRadius: 18, padding: 16, marginBottom: 24, boxShadow: '0 4px 16px rgba(31,38,135,0.06)' },
  progressBar: { height: 10, background: 'rgba(102,126,234,0.1)', borderRadius: 5, overflow: 'hidden' },
  progressFill: { height: '100%', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', borderRadius: 5, transition: 'width 0.8s' },

  sectionTitle: { fontSize: 18, fontWeight: 700, marginBottom: 14, color: '#1a1a2e' },

  stepCard: { display: 'flex', gap: 14, alignItems: 'center', padding: '14px 18px', background: 'rgba(255,255,255,0.6)', backdropFilter: 'blur(16px)', border: '1px solid rgba(255,255,255,0.6)', borderRadius: 18, boxShadow: '0 4px 16px rgba(31,38,135,0.05)' },
  stepNum: { width: 44, height: 44, borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  stepTitle: { fontSize: 15, fontWeight: 700, color: '#1a1a2e', marginBottom: 4 },
  stepMeta: { display: 'flex', gap: 12, fontSize: 11, color: '#8b8fa8', flexWrap: 'wrap' },
};

export default LearningPathDetailPage;
